import { FC, Fragment, lazy, Suspense } from "react";
import { useRoutes, Navigate } from "react-router-dom";
import { RouteUrls } from "Constant/RouteUrls";
import { isProviderReassignmentEnabled } from "Constant/FeatureFlags";
import ProtectedRoute from "ProtectedRoute";
import RouteFallback from "RouteFallback";

///UnProtected Routes
const Login = lazy(() => import("./Pages/Login"));
const ForgotPassword = lazy(() => import("./Pages/Forgot"));
const PasswordRecovery = lazy(() => import("Pages/PasswordRecovery"));
const SignUp = lazy(() => import("Pages/SignUp"));

///Protected Routes
import Home from "./Pages/Home";
const HomeScreen = lazy(() => import("./Pages/Home/HomeScreen"));
import HeadScreen from "Pages/Home/HeadScreen";
const AllPatients = lazy(() => import("Pages/Home/HeadScreen/AllPatients"));
const ChangePassword = lazy(() =>
  import("Pages/Home/HeadScreen/ChangePassword")
);
const MyProfile = lazy(() => import("Pages/Home/HeadScreen/MyProfile"));
const UserAudit = lazy(() => import("Pages/Home/HeadScreen/UserAudit"));
const Notifications = lazy(() =>
  import("Pages/Home/HeadScreen/Notifications")
);
const AboutUs = lazy(() => import("Pages/Home/HeadScreen/AboutUs"));

///Master
import Master from "Pages/Home/HeadScreen/Master";
const MasterProgram = lazy(() =>
  import("Pages/Home/HeadScreen/Master/Program")
);
import MasterProblems from "Pages/Home/HeadScreen/Master/Problem";
const DeviceMaster = lazy(() => import("Pages/Home/HeadScreen/Master/Device"));
const ProblemList = lazy(() =>
  import("Pages/Home/HeadScreen/Master/Problem/List")
);
const ProblemICDCodes = lazy(() =>
  import("Pages/Home/HeadScreen/Master/Problem/Codes")
);
const ProgramProblem = lazy(() =>
  import("Pages/Home/HeadScreen/Master/PrgmProbMapper")
);
const DeviceProblem = lazy(() =>
  import("Pages/Home/HeadScreen/Master/DeviceProbMapper")
);
const QuesCategory = lazy(() =>
  import("Pages/Home/HeadScreen/Master/Category")
);
import MasterQuestion from "Pages/Home/HeadScreen/Master/Questionnaire";
const QuestionList = lazy(() =>
  import("Pages/Home/HeadScreen/Master/Questionnaire/List")
);
const QuestionUpdate = lazy(() =>
  import("Pages/Home/HeadScreen/Master/Questionnaire/Update")
);

///Settings Master
const SmartmeterSettings = lazy(() =>
  import("Pages/Home/HeadScreen/Settings/Smartmeter")
);
import SettingManagement from "Pages/Home/HeadScreen/Settings";
const RedoxSettings = lazy(() =>
  import("Pages/Home/HeadScreen/Settings/Redox")
);
const ApplicationSettings = lazy(() =>
  import("Pages/Home/HeadScreen/Settings/Application")
);
const GlobalThresholdAlerts = lazy(() =>
  import("Pages/Home/HeadScreen/Settings/GlobalAlerts")
);
const AlertSettings = lazy(() =>
  import("Pages/Home/HeadScreen/Settings/AlertSettings")
);

///Patient Management
import PatientMgmt from "Pages/Home/HeadScreen/PatientMgmt";
const Details = lazy(() => import("Pages/Home/HeadScreen/PatientMgmt/Details"));
const ClinicalSummary = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/Summary")
);
const CallLogs = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CallLog")
);
const DischargeSummary = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/DischargeSummary")
);
const ConsentGained = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/ConsentGained")
);
import CarePlan from "Pages/Home/HeadScreen/PatientMgmt/CarePlan";
const CareProblems = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CarePlan/Problems")
);
const CarePlanQuestionnaire = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CarePlan/Questions")
);
const AssignTeam = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CarePlan/CareTeam")
);
const EditTeam = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CarePlan/EditTeam")
);
const CarePlanSummary = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CarePlan/Summary")
);
import Recurring from
  "Pages/Home/HeadScreen/PatientMgmt/CarePlan/Recurring";
const RPMDeviceCharts = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CarePlan/Recurring/RPMDeviceCharts")
);

///List Management Screens
import ListMgmt from "Pages/Home/HeadScreen/ListMgmt";
const Eligible = lazy(() => import("Pages/Home/HeadScreen/ListMgmt/Eligible"));
const PendingAssignment = lazy(() =>
  import("Pages/Home/HeadScreen/ListMgmt/PendingAssignment")
);
const Enrolled = lazy(() => import("Pages/Home/HeadScreen/ListMgmt/Enrolled"));
const MyPatients = lazy(() =>
  import("Pages/Home/HeadScreen/ListMgmt/MyPatients")
);
const PendingProviderApproval = lazy(() =>
  import("Pages/Home/HeadScreen/ListMgmt/PendingProviderApproval")
);
const PendingCare = lazy(() =>
  import("Pages/Home/HeadScreen/ListMgmt/PendingCare")
);
const CompleteCare = lazy(() =>
  import("Pages/Home/HeadScreen/ListMgmt/CompleteCare")
);
const Inactive = lazy(() => import("Pages/Home/HeadScreen/ListMgmt/Inactive"));
const Ongoing = lazy(() => import("Pages/Home/HeadScreen/ListMgmt/Ongoing"));
const Billing = lazy(() => import("Pages/Home/HeadScreen/ListMgmt/Billing"));
const Consent = lazy(() => import("Pages/Home/HeadScreen/ListMgmt/Consent"));

///Practice Management
import OrgMgmt from "Pages/Home/HeadScreen/OrgMgmt";
const OrganizationList = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/List")
);
const OrganizationUpdate = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/Update")
);
const PreApprovedPlan = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/PreApprovedPlan")
);
import PreApprovedPlanLayout from
  "Pages/Home/HeadScreen/OrgMgmt/PreApprovedPlan/Layout";
const PlanDetails = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/PreApprovedPlan/PlanDetails")
);
import OrganizationStaff from "Pages/Home/HeadScreen/OrgMgmt/Staff";
const OrganizationStaffList = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/Staff/List")
);
const OrganizationStaffUpdate = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/Staff/Update")
);
const PracticeInsuranceMaster = lazy(() =>
  import("Pages/Home/HeadScreen/Master/PracticeInsuranceMaster")
);
const PatientThresholdAlerts = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/Thresholds")
);

///Task Management
import TaskMgmt from "Pages/Home/HeadScreen/TaskMgmt";
const IncomingTasks = lazy(() =>
  import("Pages/Home/HeadScreen/TaskMgmt/Incoming")
);
const OutgoingTasks = lazy(() =>
  import("Pages/Home/HeadScreen/TaskMgmt/Outgoing")
);
const GroupTasks = lazy(() => import("Pages/Home/HeadScreen/TaskMgmt/Group"));
const GlobalTasks = lazy(() => import("Pages/Home/HeadScreen/TaskMgmt/Global"));
const Task = lazy(() => import("Pages/Home/HeadScreen/PatientMgmt/Task"));
const PatientAuditTrail = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/PatientAudit")
);
const AddOnTimeLog = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/AddOnTimeLog")
);
const PatientImport = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/PatientImport")
);
const CareDevice = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/CarePlan/CareDevice")
);
const DeviceTask = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/DeviceTask")
);

//Device Management
import Devices from "Pages/Home/HeadScreen/PatientMgmt/Devices";
const DeviceDetails = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/Devices/Details")
);
const DeviceCallLogs = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/Devices/CallLog")
);
import TrainingSection from
  "Pages/Home/HeadScreen/PatientMgmt/Devices/TrainingNotes";
const TrainingDeviceNotes = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/Devices/TrainingNotes/Notes")
);
const DeviceReadings = lazy(() =>
  import("Pages/Home/HeadScreen/PatientMgmt/DeviceReading")
);

// Training List Management
import Trainings from "Pages/Home/HeadScreen/TrainingMgmt";
const MyTrainingList = lazy(() =>
  import("Pages/Home/HeadScreen/TrainingMgmt/MyTrainings")
);
const CompletedTrainingList = lazy(() =>
  import("Pages/Home/HeadScreen/TrainingMgmt/CompletedTrainings")
);

//Revision
import RevisionPlan from
  "Pages/Home/HeadScreen/PatientMgmt/CarePlan/revision";

const PendingDeviceList = lazy(() =>
  import("Pages/Home/HeadScreen/PendingDevice")
);

///RPM Alert Management
import RPMAlerts from "Pages/Home/HeadScreen/RPMAlerts";
const AlertListUI = lazy(() =>
  import("Pages/Home/HeadScreen/RPMAlerts/AlertListUI")
);
const ProviderTransitionWorklist = lazy(() =>
  import("Pages/Home/HeadScreen/ProviderTransition")
);
const ProviderAssignedWorklist = lazy(() =>
  import("Pages/Home/HeadScreen/RPMAlerts/ProviderAssignedWorklist")
);
const NotFound = lazy(() => import("Pages/Home/HeadScreen/NotFound"));
const MyPreApprovedPlans = lazy(() =>
  import("Pages/Home/HeadScreen/MyPreApprovedPlans")
);
const MyPlanDetails = lazy(() =>
  import("Pages/Home/HeadScreen/MyPreApprovedPlans/MyPlanDetails")
);
const ManualUpload = lazy(() =>
  import("Pages/Home/HeadScreen/OrgMgmt/ManualUpload")
);

const Router: FC = () => {
  const token = localStorage.getItem("cm-token");

  const routes = useRoutes([
    { path: RouteUrls.login, element: <Login /> },
    { path: RouteUrls.forgot, element: <ForgotPassword /> },
    { path: RouteUrls.passwordRecovery, element: <PasswordRecovery /> },
    { path: `${RouteUrls.signUp}/:token`, element: <SignUp /> },
    {
      path: "/",
      element: token ? <Home /> : <Navigate to={RouteUrls.login} replace />,
      children: [
        { path: "", element: <HomeScreen /> },
        {
          path: RouteUrls.hs,
          element: <HeadScreen />,
          children: [
            { path: "", element: <Navigate to="/" /> },
            { path: RouteUrls.notfound, element: <NotFound /> },
            { path: RouteUrls.allPatients, element: <AllPatients /> },
            { path: RouteUrls.changePass, element: <ChangePassword /> },
            { path: RouteUrls.aboutUs, element: <AboutUs /> },
            {
              path: `${RouteUrls.myPreApprovedPlans}/:planId` +
                `/:versionId/${RouteUrls.plan}`,
              element: <MyPlanDetails />,
            },
            {
              path: `${RouteUrls.myPreApprovedPlans}/:planId` +
                `/${RouteUrls.plan}`,
              element: <MyPlanDetails />,
            },
            {
              path: RouteUrls.myPreApprovedPlans,
              element: <MyPreApprovedPlans />
            },
            {
              path: RouteUrls.list,
              element: <ListMgmt />,
              children: [
                {
                  path: "",
                  element: <Navigate to={RouteUrls.eligible} />,
                },
                {
                  path: RouteUrls.eligible,
                  element: <Eligible />,
                },
                {
                  path: RouteUrls.pendingAssign,
                  element: <PendingAssignment />,
                },
                {
                  path: RouteUrls.enrolled,
                  element: <Enrolled />,
                },
                {
                  path: RouteUrls.myPatients,
                  element: <MyPatients />,
                },
                {
                  path: RouteUrls.pendingApproval,
                  element: <PendingProviderApproval />,
                },
                {
                  path: RouteUrls.pendingCare,
                  element: <PendingCare />,
                },
                {
                  path: RouteUrls.completedCare,
                  element: <CompleteCare />,
                },
                {
                  path: RouteUrls.inactive,
                  element: <Inactive />,
                },
                {
                  path: RouteUrls.ongoing,
                  element: <Ongoing />,
                },
                {
                  path: RouteUrls.billing,
                  element: <Billing />,
                },
                {
                  path: RouteUrls.consent,
                  element: <Consent />,
                },
              ],
            },
            {
              path: RouteUrls.master,
              element: <ProtectedRoute Child={Master} roleIds={[1]} />,
              children: [
                { path: "", element: <Navigate to={RouteUrls.category} /> },
                { path: RouteUrls.category, element: <QuesCategory /> },
                {
                  path: RouteUrls.practiceInsMaster,
                  element: <PracticeInsuranceMaster />,
                },
                { path: RouteUrls.prgmProbMap, element: <ProgramProblem /> },
                { path: RouteUrls.deviceProbMap, element: <DeviceProblem /> },
                {
                  path: RouteUrls.prob,
                  element: <MasterProblems />,
                  children: [
                    { path: "", element: <Navigate to={RouteUrls.list} /> },
                    { path: RouteUrls.list, element: <ProblemList /> },
                    { path: RouteUrls.icdCodes, element: <ProblemICDCodes /> },
                  ],
                },
                { path: RouteUrls.device, element: <DeviceMaster /> },
                { path: RouteUrls.prgm, element: <MasterProgram /> },
                {
                  path: RouteUrls.questionnaire,
                  element: <MasterQuestion />,
                  children: [
                    { path: "", element: <Navigate to={RouteUrls.list} /> },
                    { path: RouteUrls.list, element: <QuestionList /> },
                    {
                      path: `:categoryId/${RouteUrls.add}`,
                      element: <QuestionUpdate />,
                    },
                    {
                      path: `:categoryId/:quesId/${RouteUrls.update}`,
                      element: <QuestionUpdate />,
                    },
                    {
                      path: `:categoryId/:parentId/:parentOptionId/${RouteUrls.add}`,
                      element: <QuestionUpdate />,
                    },
                    {
                      path: `:categoryId/:parentId/:parentOptionId/:quesId/${RouteUrls.update}`,
                      element: <QuestionUpdate />,
                    },
                  ],
                },
              ],
            },
            {
              path: RouteUrls.setting,
              element: (
                <ProtectedRoute Child={SettingManagement} roleIds={[1, 2]} />
              ),
              children: [
                {
                  path: RouteUrls.smartmeterSettings,
                  element: <SmartmeterSettings />,
                },
                { path: RouteUrls.redoxSettings, element: <RedoxSettings /> },
                {
                  path: RouteUrls.appSettings,
                  element: <ApplicationSettings />,
                },
                {
                  path: RouteUrls.globalThresholdAlerts,
                  element: <GlobalThresholdAlerts />,
                },
                {
                  path: RouteUrls.alertSettings,
                  element: <AlertSettings />,
                },
              ],
            },
            { path: RouteUrls.myProfile, element: <MyProfile /> },
            { path: RouteUrls.myOrganization, element: <OrganizationUpdate /> },
            {
              path: RouteUrls.organization,
              element: <OrgMgmt />,
              children: [
                {
                  path: "",
                  element: <Navigate to={RouteUrls.list} />,
                },
                {
                  path: RouteUrls.patientUpload,
                  element: <PatientImport />,
                },
                {
                  path: `:orgId/${RouteUrls.preApprovedPlan}`,
                  element: <PreApprovedPlanLayout />,
                  children: [
                    {
                      path: "",
                      element: <Navigate to={RouteUrls.list} replace />,
                    },
                    {
                      path: RouteUrls.list,
                      element: <PreApprovedPlan />,
                    },
                    {
                      path: `:planId/:versionId/${RouteUrls.plan}`,
                      element: <PlanDetails />,
                    },
                  ],
                },
                {
                  path: RouteUrls.manualPatientUpload,
                  element: <ManualUpload />,
                },
                { path: RouteUrls.orgList, element: <OrganizationList /> },
                { path: RouteUrls.add, element: <OrganizationUpdate /> },
                {
                  path: `:orgId/${RouteUrls.update}`,
                  element: <OrganizationUpdate />,
                },
                {
                  path: `${RouteUrls.myOrganization}/:orgId/${RouteUrls.View}`,
                  element: <OrganizationUpdate />,
                },
                {
                  path: `:orgId/${RouteUrls.staff}`,
                  element: <OrganizationStaff />,
                  children: [
                    {
                      path: "",
                      element: <Navigate to={`${RouteUrls.list}`} />,
                    },
                    {
                      path: `${RouteUrls.list}`,
                      element: <OrganizationStaffList />,
                    },
                    {
                      path: `${RouteUrls.add}`,
                      element: <OrganizationStaffUpdate />,
                    },
                    {
                      path: `:staffId/${RouteUrls.update}`,
                      element: <OrganizationStaffUpdate />,
                    },
                  ],
                },
              ],
            },
            {
              path: `${RouteUrls.patient}/:patientId`,
              element: <PatientMgmt />,
              children: [
                { path: "", element: <Navigate to={RouteUrls.details} /> },
                { path: RouteUrls.details, element: <Details /> },
                {
                  path: RouteUrls.addonTime,
                  element: <AddOnTimeLog />,
                  children: [
                    {
                      path: `:logId/${RouteUrls.View}`,
                      element: <AddOnTimeLog />,
                    },
                  ],
                },
                {
                  path: RouteUrls.thresholds,
                  element: <PatientThresholdAlerts />,
                },
                {
                  path: RouteUrls.clinicalSummary,
                  element: <ClinicalSummary />,
                },
                {
                  path: RouteUrls.deviceReading,
                  element: <DeviceReadings />,
                },
                { path: RouteUrls.callLog, element: <CallLogs /> },
                {
                  path: RouteUrls.patientAudit,
                  element: <PatientAuditTrail />,
                },
                {
                  path: `${RouteUrls.task}/${RouteUrls.deviceAddTask}`,
                  element: <DeviceTask />,
                },
                {
                  path: `${RouteUrls.tasking}/:taskId/${RouteUrls.deviceUpdateTask}`,
                  element: <DeviceTask />,
                },
                {
                  path: `${RouteUrls.task}/:taskId/${RouteUrls.deviceViewTask}`,
                  element: <DeviceTask />,
                },
                {
                  path: `${RouteUrls.task}/${RouteUrls.add}`,
                  element: <Task />,
                },
                {
                  path: `${RouteUrls.tasking}/${RouteUrls.incoming}/:taskId/${RouteUrls.update}`,
                  element: <Task />,
                },
                {
                  path: `${RouteUrls.tasking}/${RouteUrls.outgoing}/:taskId/${RouteUrls.update}`,
                  element: <Task />,
                },
                {
                  path: `${RouteUrls.tasking}/${RouteUrls.global}/:taskId/${RouteUrls.update}`,
                  element: <Task />,
                },
                {
                  path: `${RouteUrls.task}/:taskId/${RouteUrls.View}`,
                  element: <Task />,
                },
                { path: RouteUrls.consent, element: <ConsentGained /> },
                { path: RouteUrls.program, element: <ConsentGained /> },
                {
                  path: RouteUrls.dischargeSummary,
                  element: <DischargeSummary />,
                },
                { path: RouteUrls.rpmDeviceList, element: <CareDevice /> },
                {
                  path: `${RouteUrls.carePlan}/:careplanId`,
                  element: <CarePlan />,
                  children: [
                    {
                      path: "",
                      element: <Navigate to={RouteUrls.careProblem} />,
                    },
                    { path: RouteUrls.careProblem, element: <CareProblems /> },
                    {
                      path: RouteUrls.careQuestion,
                      element: <CarePlanQuestionnaire />,
                    },
                    { path: RouteUrls.careTeam, element: <AssignTeam /> },
                    { path: RouteUrls.editTeam, element: <EditTeam /> },
                    {
                      path: RouteUrls.careSummary,
                      element: <CarePlanSummary />,
                    },
                    {
                      path: `${RouteUrls.recurring}/:recurringId`,
                      element: <Recurring />,
                      children: [
                        {
                          path: "",
                          element: <Navigate to={RouteUrls.careQuestion} />,
                        },
                        {
                          path: RouteUrls.careQuestion,
                          element: <CarePlanQuestionnaire />,
                        },
                        {
                          path: RouteUrls.careSummary,
                          element: <CarePlanSummary />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: `${RouteUrls.treatmentPlan}/:careplanId`,
                  element: <CarePlan />,
                  children: [
                    {
                      path: "",
                      element: <Navigate to={RouteUrls.careProblem} />,
                    },
                    { path: RouteUrls.careProblem, element: <CareProblems /> },
                    { path: RouteUrls.rpmAddDevice, element: <CareDevice /> },
                    {
                      path: RouteUrls.careQuestion,
                      element: <CarePlanQuestionnaire />,
                    },
                    { path: RouteUrls.careTeam, element: <AssignTeam /> },
                    {
                      path: RouteUrls.careSummary,
                      element: <CarePlanSummary />,
                    },
                    {
                      path: RouteUrls.deviceReading,
                      element: <RPMDeviceCharts />,
                    },
                    {
                      path: `${RouteUrls.recurring}/:recurringId`,
                      element: <Recurring />,
                      children: [
                        {
                          path: "",
                          element: <Navigate to={RouteUrls.rpmrecurring} />,
                        },
                        {
                          path: RouteUrls.rpmrecurring,
                          element: <RPMDeviceCharts />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: `${RouteUrls.revisionPlan}/:revisionPlanId`,
                  element: <RevisionPlan />,
                  children: [
                    {
                      path: "",
                      element: <Navigate to={RouteUrls.careProblem} />,
                    },
                    { path: RouteUrls.careProblem, element: <CareProblems /> },
                    { path: RouteUrls.rpmAddDevice, element: <CareDevice /> },
                    {
                      path: RouteUrls.careQuestion,
                      element: <CarePlanQuestionnaire />,
                    },
                    { path: RouteUrls.careTeam, element: <AssignTeam /> },
                    {
                      path: RouteUrls.careSummary,
                      element: <CarePlanSummary />,
                    },
                  ],
                },
                {
                  path: `${RouteUrls.tcm}/:careplanId`,
                  element: <CarePlan />,
                  children: [
                    {
                      path: "",
                      element: <Navigate to={RouteUrls.careQuestion} />,
                    },
                    {
                      path: RouteUrls.careQuestion,
                      element: <CarePlanQuestionnaire />,
                    },
                    { path: RouteUrls.careTeam, element: <AssignTeam /> },
                    {
                      path: RouteUrls.careSummary,
                      element: <CarePlanSummary />,
                    },
                  ],
                },
                {
                  path: `${RouteUrls.device}/:patientDeviceId`,
                  element: <Devices />,
                  children: [
                    {
                      path: "",
                      element: <Navigate to={RouteUrls.deviceDetails} />,
                    },
                    {
                      path: RouteUrls.deviceDetails,
                      element: <DeviceDetails />,
                    },
                    { path: RouteUrls.callLog, element: <DeviceCallLogs /> },
                    {
                      path: RouteUrls.trainingNotes,
                      element: <TrainingDeviceNotes />,
                    },
                    {
                      path: `${RouteUrls.training}/:trainingId`,
                      element: <TrainingSection />,
                      children: [
                        {
                          path: "",
                          element: <Navigate to={RouteUrls.trainingNotes} />,
                        },
                        {
                          path: RouteUrls.trainingNotes,
                          element: <TrainingDeviceNotes />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: RouteUrls.tasking,
              element: <TaskMgmt />,
              children: [
                {
                  path: "",
                  element: <Navigate to={RouteUrls.incoming} />,
                },
                {
                  path: RouteUrls.incoming,
                  element: <IncomingTasks />,
                },
                {
                  path: RouteUrls.outgoing,
                  element: <OutgoingTasks />,
                },
                {
                  path: RouteUrls.group,
                  element: <GroupTasks />,
                },
                {
                  path: RouteUrls.global,
                  element: <GlobalTasks />,
                },
              ],
            },
            {
              path: RouteUrls.training,
              element: <Trainings />,
              children: [
                {
                  path: "",
                  element: <Navigate to={RouteUrls.myTraining} />,
                },
                {
                  path: RouteUrls.myTraining,
                  element: <MyTrainingList />,
                },
                {
                  path: RouteUrls.completedTraining,
                  element: <CompletedTrainingList />,
                },
                {
                  path: RouteUrls.billing,
                  element: <Billing />,
                },
              ],
            },
            { path: RouteUrls.pendingDevice, element: <PendingDeviceList /> },
            { path: RouteUrls.userAudit, element: <UserAudit /> },
            { path: RouteUrls.notifications, element: <Notifications /> },
            ...(isProviderReassignmentEnabled
              ? [
                {
                  path: RouteUrls.providerTransition,
                  element: <ProviderTransitionWorklist />,
                },
                {
                  path: RouteUrls.providerAssigned,
                  element: <ProviderAssignedWorklist />,
                },
              ]
              : []),
            {
              path: RouteUrls.alert,
              element: <RPMAlerts />,
              children: [
                {
                  path: "",
                  element: <Navigate to={RouteUrls.unread} />,
                },
                {
                  path: RouteUrls.unread,
                  element: <AlertListUI />,
                },
                {
                  path: RouteUrls.read,
                  element: <AlertListUI />,
                },
                {
                  path: RouteUrls.addressed,
                  element: <AlertListUI />,
                },
              ],
            },
          ],
        },
      ],
    },
    { path: "*", element: <Fragment></Fragment> },
  ]);
  return <Suspense fallback={<RouteFallback />}>{routes}</Suspense>;
};
export default Router;
